import { useRef, useState } from 'react'

import { Popover as PopoverPrimitive } from '@base-ui/react/popover'

import { cn } from '../../../utils/cn'
import {
  triggerSizeVariants,
  type TriggerSize
} from '../../../utils/trigger-size-variants'
import { Icon } from '../../atoms/icon/icon'

import { DatePicker, formatDate, type DateRange } from './date-picker'

/* ---------------------------------- types --------------------------------- */

interface DatePickerPopoverBaseProps {
  placeholder?: string
  size?: TriggerSize
  error?: boolean
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  disabledDays?: (date: Date) => boolean
  confirmLabel?: string
  cancelLabel?: string
  /** Positioner side, defaults to bottom */
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  className?: string
  id?: string
  onOpenChange?: (open: boolean) => void
  onMonthChange?: (month: Date) => void
  'aria-label'?: string
  'aria-labelledby'?: string
}

export interface SingleDatePickerPopoverProps
  extends DatePickerPopoverBaseProps {
  mode?: 'single'
  value?: Date | null
  onChange?: (date: Date) => void
}

export interface RangeDatePickerPopoverProps
  extends DatePickerPopoverBaseProps {
  mode: 'range'
  value?: DateRange | null
  onChange?: (range: DateRange) => void
}

export type DatePickerPopoverProps =
  | SingleDatePickerPopoverProps
  | RangeDatePickerPopoverProps

/* -------------------------------- component ------------------------------- */

export const DatePickerPopover = (props: DatePickerPopoverProps) => {
  const {
    placeholder = 'Select a date',
    size = 'md',
    error = false,
    disabled = false,
    minDate,
    maxDate,
    disabledDays,
    confirmLabel,
    cancelLabel,
    side = 'bottom',
    align = 'start',
    className,
    id,
    onOpenChange,
    onMonthChange,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy
  } = props
  const isRange = props.mode === 'range'

  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  const setOpenAndNotify = (next: boolean) => {
    if (openRef.current === next) return
    openRef.current = next
    setOpen(next)
    onOpenChange?.(next)
  }

  const display = isRange
    ? props.value
      ? `${formatDate(props.value.from)} – ${formatDate(props.value.to)}`
      : ''
    : props.value
    ? formatDate(props.value)
    : ''

  const handleConfirm = (selected: Date | DateRange) => {
    if (isRange) {
      ;(props as RangeDatePickerPopoverProps).onChange?.(selected as DateRange)
    } else {
      ;(props as SingleDatePickerPopoverProps).onChange?.(selected as Date)
    }
    setOpenAndNotify(false)
  }

  const pickerProps = {
    minDate,
    maxDate,
    disabledDays,
    confirmLabel,
    cancelLabel,
    onCancel: () => setOpenAndNotify(false)
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpenAndNotify}>
      <PopoverPrimitive.Trigger
        data-slot='date-picker-trigger'
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={error || undefined}
        className={cn(
          'bg-surface border-outline flex w-full items-center gap-2 rounded border transition-colors',
          'cursor-pointer select-none text-left outline-none',
          'hover:border-primary-100 focus-visible:border-primary data-popup-open:border-primary',
          'disabled:bg-surface-200 disabled:hover:border-outline disabled:cursor-not-allowed',
          error && 'border-danger-icon hover:border-danger-icon',
          triggerSizeVariants({ size }),
          className
        )}
      >
        <Icon
          name='calendar_today'
          className={cn(
            'shrink-0 text-[18px]',
            error && !disabled ? 'text-danger-icon' : 'text-surface-body'
          )}
        />
        <span
          data-slot='date-picker-value'
          className={cn(
            'flex-1 truncate',
            display
              ? error
                ? 'text-danger-icon'
                : 'text-surface-title-50'
              : 'text-surface-body',
            disabled && 'text-surface-body'
          )}
        >
          {display || placeholder}
        </span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          side={side}
          align={align}
          sideOffset={4}
          // Above legacy SideDrawer stackTop (utils/theme zIndexStack =
          // 2147483002, Intercom+1). z-50 portals under that drawer, so the
          // charge-invoice calendar looked like it never opened (FIRE-2821).
          className='isolate z-[2147483003]'
        >
          <PopoverPrimitive.Popup
            data-slot='date-picker-popup'
            className={cn(
              'origin-(--transform-origin) outline-none',
              'transition-[opacity,transform] duration-150 ease-out',
              'data-[starting-style]:opacity-0 motion-safe:data-[starting-style]:scale-95',
              'data-[ending-style]:opacity-0 motion-safe:data-[ending-style]:scale-95'
            )}
          >
            {isRange ? (
              <DatePicker
                mode='range'
                value={(props.value as DateRange | null) ?? null}
                onConfirm={handleConfirm}
                {...pickerProps}
                onMonthChange={onMonthChange}
              />
            ) : (
              <DatePicker
                value={(props.value as Date | null) ?? null}
                onConfirm={handleConfirm}
                {...pickerProps}
                onMonthChange={onMonthChange}
              />
            )}
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
